import React, { useState } from "react";
import { Box, Typography, useTheme, Skeleton, Grid2, Select, MenuItem } from "@mui/material";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useGetEngineeringStatusByDepartmentQuery } from "store/slices/apis/engineeringDashboardSlice";
import { useParams } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import { ReactComponent as ChevronDownIcon } from "assets/PwC_Funct_Icons_ChevronDown_Outline_Black_RGB.svg"

const DistributionofDocumentStatusByDepartment = () => {
    const theme = useTheme();
    const { projectId } = useParams();
    const { data, isLoading, error } = useGetEngineeringStatusByDepartmentQuery({ projectId });
    const categories = [...new Set(data?.map(item => item.category))] || [];
    const statuses = [...new Set(data?.map(item => item.status))] || [];
    const [selectedCategory, SetSelectedCategory] = useState("All")

    const colorPalette = [
        "#295477", "#E72B33 ", "#FDCA41", "#3E756D", "#CE412D",
        "#57A773", "#A76D60", "#6E44FF", "#FF6B6B", "#00A9A5"
    ];

    const statusColorMap = statuses.reduce((acc, status, index) => {
        acc[status] = colorPalette[index % colorPalette.length];
        return acc;
    }, {});

    const series = statuses.map((status) => {
        const seriesData = selectedCategory === 'All' ? categories.map((category) => {
            const match = data.find(item => item.status === status && item.category === category);
            return match ? match.count : 0;
        })
            : (() => {
                const match = data.find(item => item.status === status && item.category === selectedCategory);
                return [match ? match.count : 0];
            })();

        return {
            name: status,
            data: seriesData,
            color: statusColorMap[status],
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#fff',
                    textOutline: 'none'
                }
            }
        };
    });


    const onLegendClick = function () {

        const myName = this.name;
        const myVis = !this.visible;

        this.chart.series.forEach((s) => {
            if (s.name === myName) {
                s.setVisible(myVis, false);
            }
        });
        this.chart.redraw();
        return false;
    };




    let chartOptions = {
        chart: {
            type: 'column',
            height: 330,
            marginBottom: 40
        },
        title: {
            text: null, // ✅ No chart title in center
        },
        xAxis: {
            categories: categories,
            tickmarkPlacement: 'on',
            tickWidth: 1,
            tickLength: 10,
            tickColor: '#999',
            lineWidth: 1,
            lineColor: '#758195',
            labels: {
                style: {
                    fontWeight: 500,
                    fontSize: '12px',
                    color: '#414D55',
                    opacity: 0.5,
                    lineHeight: '100%',
                },
            },
        },
        yAxis: {
            min: 0,
            gridLineWidth: 1,
            tickInterval: 50,
            tickWidth: 1,
            tickLength: 10,
            tickColor: '#999',
            lineWidth: 1,
            lineColor: "#758195",
            title: {
                text: "",
            },
            labels: {
                style: {
                    fontWeight: 500,
                    fontSize: '10px',
                    color: '#414D55',
                    opacity: 0.5,
                    lineHeight: '100%',
                },
            },
        },
        legend: {
            enabled: true,
            align: 'left',
            verticalAlign: 'top',
            reversed: true,
            layout: 'horizontal',
            itemDistance: 20,
            symbolRadius: 0,
            symbolHeight: 12,
            symbolWidth: 12,
            margin: 43,
            itemStyle: {
                fontWeight: 500,
                fontSize: '13px',
                color: "#182A4E"
            },
        },
        tooltip: {
            pointFormat: '{series.name}: <b>{point.y}</b><br/>',
            shared: true,
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                borderWidth: 0,
                dataLabels: {
                    enabled: true,
                    color: '#FFFFFF',
                    style: {
                        fontWeight: 'bold',
                    },
                },
            },
            series: {
                events: {
                    legendItemClick: onLegendClick,
                },
            },
        },
        credits: {
            enabled: false,
        },
        series: series

    };

     const CustomChevronIcon = (props) => (
            <ChevronDownIcon style={{ width: '16px', height: '16px' }} {...props} />
        );

    return (
        <Box sx={{ padding: 2, background: "#fff", borderRadius: 2, height: "500px", width: "100%", minHeight: "100%", border: '1px solid #2525252E', }}>
            <Grid2 container  >
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Typography variant="body" sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: '-0.4px', color: `${theme?.customSettings?.themeColor}` }}>
                        Distribution of Document Status by Department
                    </Typography>
                    <Typography sx={{ mt: "5px", color: "#5052588A", fontSize: "14px", mb: "20px", lineHeight: "100%", letterSpacing: '-0.4px', whiteSpace: { xs: "normal", md: "nowrap" }, }}>
                        Departmental document flow to identify bottlenecks and streamline approvals
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Box sx={{ width: '100%', display: "flex", justifyContent: 'end' }}>
                        <Select
                            labelId="demo-simple-select-standard-label"
                            id="demo-simple-select-standard"
                            value={selectedCategory}
                            onChange={(e) => SetSelectedCategory(e.target.value)}
                            // IconComponent={IoIosArrowDown}
                            IconComponent={CustomChevronIcon}
                            sx={{
                                backgroundColor: '#E8E8E8',
                                '& .MuiOutlinedInput-input': {
                                    padding: '5px 15px'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                }
                            }}
                        >
                            <MenuItem value='All'>All</MenuItem>
                            {
                                categories.map((item, index) => (
                                    <MenuItem key={index} value={item}>{item}</MenuItem>

                                ))
                            }
                        </Select>
                    </Box>
                </Grid2>
            </Grid2>

            <Grid2 container>
                <Grid2 size={{ xs: 12, md: 12 }}>
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default DistributionofDocumentStatusByDepartment;
